import { useEjectSubQuiz } from "@/lib/hooks/slides/editor/useEjectSubQuiz";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import type { ReadingSlideData, SubQuiz } from "@ai-teach/types";
import { SubQuizSection } from "../../shared/SubQuizSection";

/**
 * Fluency post-reading quiz authoring. Rendered only for fluency slides (the editor gates it).
 * Thin Redux wrapper around the shared `SubQuizSection` — see that file for the actual form UI.
 */
export function FluencyQuizSection() {
  const dispatch = useAppDispatch();
  const { slide } = useAppSelector((state) => state.slideEditor.present);
  const { ejectSubQuiz } = useEjectSubQuiz();

  if (!slide) return null;

  const readingSlideData = slide.slideData as ReadingSlideData;
  const quizzes = readingSlideData.subQuizzes ?? [];

  const commit = (next: SubQuiz[]) => {
    dispatch(
      updateSlide({ slideId: slide.id, slideData: { ...readingSlideData, subQuizzes: next } })
    );
  };

  // Create the standalone slide first; only drop the quiz here once it exists. The removal rides the
  // editor's next Save (see useEjectSubQuiz), so the slide must still be saved afterwards.
  const handleEject = async (quiz: SubQuiz) => {
    const ejected = await ejectSubQuiz(quiz, slide.lessonId, slide.tagIds, slide.domains);
    if (ejected) commit(quizzes.filter((q) => q.id !== quiz.id));
  };

  return (
    <SubQuizSection
      quizzes={quizzes}
      onChange={commit}
      label="Post-reading quizzes"
      onEject={handleEject}
    />
  );
}
